import React, { useState } from 'react';
import { Container } from '@common/components/container';
import { Bar } from '@common/components/bar';
import Dropdown from '@common/components/dropdown';
import CategoryModal from '@modules/performance/components/update-modal/category-modal';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { useTranslation } from 'react-i18next';
import { Accordion } from '@szhsin/react-accordion';
import DataTable from '@common/components/datatable';
import useColumns from './useColumns';
import { deleteCompetancy } from '@modules/performance/action/delete-competancy';
import { deleteCategory } from '@modules/performance/action/delete-category';
import { AlertService } from '@common/services/alert';
import { Predicates } from '@modules/performance/types';
import ConfirmButton from '@common/components/confirm-button';
import Icon from '@common/components/icon';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import AccordionItem from '@modules/performance/components/accordion-item/accordion-item';
import Spinner from '@common/components/spinner';
import { Button } from '@common/components/button';
import { useHistory, useRouteMatch } from 'react-router';
import Placeholder from '@common/components/placeholder/placeholder';

const AllCompetancy = ({
  loading,
  competency,
  setCompetency,
  predicates,
  setPredicates,
  setSearch,
  refetch
}: {
  loading: boolean;
  competency: any[];
  setCompetency: (competency: any[]) => void;
  predicates: Predicates;
  setPredicates: (predicates: Predicates) => void;
  setSearch: (search: string) => void;
  refetch: () => void;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const organisation = useAppSelector(selected);
  const { networkId } = match.params as { networkId: string };
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const [showEditModal, setShowEditModal] = useState<any>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleCloseEditModal = () => {
    setShowEditModal(null);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleDeleteCategory = async (groupData: any) => {
    if (groupData.groupType !== 'category') {
      return;
    }

    try {
      await deleteCategory(organisation.id, groupData.group.id);
      setCompetency(
        competency.filter(
          (group) => !(
              group.groupType === 'category' &&
              group.group.id === groupData.group.id
            )
        )
      );
      AlertService.success(
        t('performance:category_deleted_successfully', {
          category: groupData.group.name?.string || groupData.group.name,
        })
      );
    } catch (error) {
      AlertService.error(t('performance:category_delete_error'));
    }
  };

  const handleDeleteCompetancy = async (competencyData: any) => {
    try {
      await deleteCompetancy(organisation.id, competencyData.id);
      refetch();
      // setCompetency(
      //   competency
      //     .map((group) => ({
      //       ...group,
      //       competencies: group.competencies.filter(
      //         (comp: any) => comp.id !== competencyData.id
      //       ),
      //     }))
      //     .filter((group) => group.competencies.length > 0)
      // );

      AlertService.success(
        t('performance:competency_deleted_successfully', {
          competency: competencyData.name,
        })
      );
    } catch (error) {
      AlertService.error(t('performance:category_delete_error'));
    }
  };

  const columnOptions = {
    handleDeleteCompetancy,
  };

  const columns = useColumns(columnOptions);

  const EmptyView = () => {
    return (
      <div
        className="tw-flex tw-flex-col tw-items-center tw-justify-center !tw-h-full
  tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-p-4"
      >
        <div
          className="tw-flex tw-flex-col tw-w-[310px]
    tw-items-center tw-justify-center tw-gap-2
    tw-text-center"
        >
          <Icon
            type="briefcase_job"
            size={48}
            className="tw-text-gray-textGray"
          />
          <p className="tw-text-center tw-text-gray-muted tw-text-base">
            {t('performance:no_competencies_description')}
          </p>
        </div>
        <Button
          icon="add"
          type="primary"
          size="large"
          onClick={() => {
            history.push(
              `${basePath}/performance/competency-library/new-competency`
            );
          }}
        >
          {t('performance:new_competency')}
        </Button>
      </div>
    );
  };

  return (
    <Container.Content>
      <Bar>
        <FiltersToolbar
          predicates={predicates}
          setPredicates={setPredicates}
          organisation={organisation}
          onSearch={setSearch}
          searchPlaceholder={t('common:search_list_search_placeholder')}
        />
        <Button type="primary" size="large" onClick={() => setShowCreateModal(true)}>
          {t('performance:add_category')}
        </Button>
      </Bar>

      {loading ? (
        <Spinner centered />
      ) : (
        <Accordion transition transitionTimeout={250}>
          {competency?.map((groupData, index) => {
            const isCategory = groupData.groupType === 'category';
            const isFunction = groupData.groupType === 'function';
            const groupName =
              groupData.group.name?.string || groupData.group.name;

            return (
              <AccordionItem
                className="!tw-bg-white !tw-mb-4"
                initialEntered={index === 0}
                header={
                  isFunction && groupName === ''
                    ? t('performance:no_function_assigned')
                    : isCategory && groupName === null
                    ? t('performance:no_category_assigned')
                    : groupName
                }
                actions={
                  (
                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-4 tw-mr-3">
                      <div className="tw-text-gray-textDark tw-text-base tw-font-medium">
                        {t(`performance:competencies_count${groupData.competencies.length > 1 ? '_plural' : ''}`,
                          { count: groupData.competencies.length })}
                      </div>
                      {!isFunction ? (
                        <Dropdown
                          alignRight
                          trigger={
                            <Icon
                              type="three_dot_horiz"
                              size={18}
                              className="tw-text-gray-muted"
                            />
                      }
                        >
                          <Dropdown.Item
                            icon="edit__filled"
                            className="!tw-text-gray-textDark"
                            onClick={() => {
                          setShowEditModal(groupData);
                        }}
                          >
                            {t('common:edit')}
                          </Dropdown.Item>
                          <ConfirmButton
                            danger
                            title={t('performance:delete_category')}
                            description={t(
                          'performance:delete_category_description'
                        )}
                            onConfirm={() => handleDeleteCategory(groupData)}
                            confirmText={t('performance:delete_category')}
                          >
                            <Dropdown.Item
                              icon="delete__filled"
                              className="!tw-text-gray-textDark"
                            >
                              {t('common:delete')}
                            </Dropdown.Item>
                          </ConfirmButton>
                        </Dropdown>
                  ) : null}
                    </div>
                )
                }
              >
                <DataTable
                  columns={columns} data={groupData.competencies}
                  emptyView={
                    <Placeholder
                      title={t('performance:no_competencies_found_for_category')}
                    />
                }
                />
              </AccordionItem>
            );
          })}
          {competency.length === 0 && !loading && <EmptyView />}
        </Accordion>
      )}

      {showEditModal && (
        <CategoryModal
          category={showEditModal}
          setCompetency={setCompetency}
          onClose={handleCloseEditModal}
        />
      )}

      {showCreateModal && (
        <CategoryModal
          setCompetency={setCompetency}
          onClose={handleCloseCreateModal}
        />
      )}
    </Container.Content>
  );
};

export default AllCompetancy;
